// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What is wrong with the machine, while it is being built (M9-05, 11 §3.1).
//
// "A validation overlay is **always on**: unreachable regions shade red, ball traps outline in
// amber, a flipper geometry violation draws the offending measurement with the failing inequality
// printed next to it."
//
// **The overlay draws the pass that refuses the machine, not a second opinion.** `analyseGeometry`'s
// flood fill is the thing that will reject a publish; `analyseWithReachability` hands out the very
// grid it filled, from the same pass that produced the complaints. An overlay computed separately would be a picture of a different machine — and the author
// would believe it, because it is on the screen in front of them and the refusal is a milestone
// away.
//
// **Recomputed when the machine changes, not every frame.** The fill rasterises the whole playfield
// at 2.5 mm and is milliseconds rather than microseconds. That is nothing against a keystroke and
// far too much against seventy-two frames a second, so it is memoised on the definition object — the
// draft is immutable, so a new definition is exactly the signal that it has to run again.
//
// **Warnings and errors are both drawn, and told apart.** An unreachable device is a warning: the
// machine is legal and the author probably did not mean it. A trap is an error: the ball goes in and
// the game ends there. Drawing them the same would make the overlay a wall of colour nobody reads.

import {
  analyseWithReachability,
  type GeometryIssue,
  type MachineDefinition,
  type ReachabilityMap,
} from '../../core/ts/machine'

export interface MachineReport {
  readonly issues: readonly GeometryIssue[]
  readonly map: ReachabilityMap | undefined
  readonly errors: number
  readonly warnings: number
}

/**
 * The last report, kept against the definition it was computed from.
 *
 * One entry, not a cache: the Builder edits one machine, so a second entry would only ever hold the
 * definition before the last keystroke. A `WeakMap` would keep every intermediate definition alive
 * as long as the draft's undo history holds it, which is fifty of them.
 */
let lastDefinition: MachineDefinition | undefined
let lastReport: MachineReport | undefined

/** Analyse a machine, or hand back the analysis of the one that was analysed last. */
export function reportFor(definition: MachineDefinition): MachineReport {
  if (definition === lastDefinition && lastReport !== undefined) return lastReport
  // One pass for both, or the playfield is rasterised twice per keystroke to produce two copies of
  // the same fill.
  const { issues, map } = analyseWithReachability(definition)
  const report: MachineReport = {
    issues,
    map,
    errors: issues.filter((issue) => issue.severity === 'error').length,
    warnings: issues.filter((issue) => issue.severity === 'warning').length,
  }
  lastDefinition = definition
  lastReport = report
  return report
}

/**
 * Which device an issue is about, or nothing.
 *
 * The paths are `devices[3].spline` and the like, so this is the one place that parses them. A list
 * of complaints an author cannot click through to is a list they read once and stop reading.
 */
export function deviceIndexOf(issue: GeometryIssue): number | undefined {
  const match = /^devices\[(\d+)\]/u.exec(issue.path)
  if (match === null) return undefined
  return Number(match[1])
}

/** …and which layer. Same reason. */
export function layerIndexOf(issue: GeometryIssue): number | undefined {
  const match = /^layers\[(\d+)\]/u.exec(issue.path)
  if (match === null) return undefined
  return Number(match[1])
}

/**
 * A short name for an issue, for a list that has to fit beside a canvas.
 *
 * The full message is a paragraph and belongs on the row's `title`, where an author who wants the
 * whole sentence can have it; a list of paragraphs is a list nobody scans.
 */
export function shortLabel(issue: GeometryIssue): string {
  switch (issue.code) {
    case 'ball-trap':
      return 'Ball trap'
    case 'device-unreachable':
      return 'Unreachable'
    case 'colliders-overlap':
      return 'Overlapping'
    case 'eject-blocked':
      return 'Eject blocked'
    case 'flipper-outboard-gap':
      return 'Outboard gap'
    case 'flippers-interpenetrate':
      return 'Flippers touch'
    case 'layer-exit-cycle':
      return 'Exit loops'
    case 'layer-exit-dead-end':
      return 'Exit goes nowhere'
    case 'layer-hits-glass':
      return 'Hits the glass'
    case 'no-ball-entry':
      return 'No way in'
    case 'no-centre-drain':
      return 'No centre drain'
    case 'no-drain':
      return 'No drain'
    case 'ramp-not-climbable':
      return 'Cannot be shot'
    default:
      return issue.code
  }
}
